/**
 * OnboardingGate
 * Renders the wizard as a fixed full-screen overlay when:
 *   1. The user is an administrator, AND
 *   2. The user context API returned no companies, OR
 *   3. The wizard has been manually re-opened (isWizardOpen === true)
 *
 * Sits above everything else. Does not affect routing or AppLayout.
 */

import { useOnboarding } from '@/contexts/onboarding-context';
import { useUserContext } from '@/hooks/useUserContext';
import { OnboardingWizard } from './onboarding-wizard';

export function OnboardingGate() {
  const { hasDismissedOnboarding, hasCompletedOnboarding, isWizardOpen, savedStep } = useOnboarding();
  const { data: userContext, isLoading } = useUserContext();

  // Don't render while user context is still loading
  if (isLoading) return null;

  const isAdmin = userContext?.role?.toLowerCase() === 'administrator';
  if (!isAdmin) return null;

  const hasNoCompanies = !userContext?.companies || userContext.companies.length === 0;
  // Auto-show when no companies, unless the user explicitly skipped or fully completed.
  // The sidebar "Resume Onboarding" button lets them re-enter after skipping.
  const shouldShow = (hasNoCompanies && !hasDismissedOnboarding && !hasCompletedOnboarding) || isWizardOpen;
  if (!shouldShow) return null;

  const firstName = userContext?.firstName ?? '';
  const lastName = userContext?.lastName ?? '';
  const userName = [firstName, lastName].filter(Boolean).join(' ') || userContext?.email || '';

  const existingCompanyCount = userContext?.companies?.length ?? 0;

  return <OnboardingWizard userName={userName} initialCompanyCount={existingCompanyCount} initialStep={savedStep} />;
}
